import { useCallback, useRef, useState, type ButtonHTMLAttributes, type ReactNode } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";
import { haptic } from "@/lib/telegram";

type Ripple = { id: number; x: number; y: number };

/** Gemini "Neural Expressive" CTA: slow blue -> red -> yellow -> green gradient + glow ripple. */
export function NeuralButton({
  children,
  className,
  onPointerDown,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { children: ReactNode }) {
  const [ripples, setRipples] = useState<Ripple[]>([]);
  const counter = useRef(0);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const rect = event.currentTarget.getBoundingClientRect();
      const id = counter.current++;
      setRipples((prev) => [...prev, { id, x: event.clientX - rect.left, y: event.clientY - rect.top }]);
      window.setTimeout(() => setRipples((prev) => prev.filter((r) => r.id !== id)), 650);
      haptic("light");
      onPointerDown?.(event);
    },
    [onPointerDown],
  );

  return (
    <motion.button
      whileTap={{ scale: 0.95 }}
      transition={{ type: "spring", stiffness: 520, damping: 18 }}
      onPointerDown={handlePointerDown}
      className={cn(
        "neural-gradient neural-halo relative isolate z-0 inline-flex h-13 items-center justify-center gap-2 overflow-hidden rounded-full px-6 py-3.5",
        "text-[15px] font-bold tracking-tight text-white shadow-[0_18px_40px_-18px_rgba(0,0,0,0.9)]",
        "disabled:cursor-not-allowed disabled:opacity-50",
        className,
      )}
      {...(props as React.ComponentProps<typeof motion.button>)}
    >
      <span className="relative z-10 inline-flex items-center justify-center gap-2">{children}</span>
      {ripples.map((r) => (
        <span
          key={r.id}
          aria-hidden
          className="pointer-events-none absolute z-0 size-24 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/45"
          style={{ left: r.x, top: r.y, animation: "ripple-out 620ms ease-out forwards" }}
        />
      ))}
    </motion.button>
  );
}
